import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useCrmConnections } from '../../hooks/useCrmConnections'
import { useEnrichment } from '../../hooks/useEnrichment'
import { useEnrichmentTeam, useSharedTeam } from '../../hooks/useTeam'
import { useTeamEnrichmentProvider } from '../../hooks/useTeamEnrichmentProvider'
import { db } from '../../lib/db'
import { backfillInvitations } from '../../lib/crm'

function formatRelative(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return mins + ' min ago'
  const hours = Math.floor(mins / 60)
  if (hours < 24) return hours + ' hour' + (hours === 1 ? '' : 's') + ' ago'
  const days = Math.floor(hours / 24)
  return days + ' day' + (days === 1 ? '' : 's') + ' ago'
}

export default function CrmSettings() {
  const { connections, loading, error, extensionReady, connect, disconnect, test } = useCrmConnections()
  const [affinityKey, setAffinityKey] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [testing, setTesting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [teamEnrichmentKey, setTeamEnrichmentKey] = useState('')

  const affinity = connections.affinity
  const sharedTeam = useSharedTeam()
  const enrichmentTeam = useEnrichmentTeam()
  const usingSoloEnrichment = !!enrichmentTeam?.is_personal
  const teamEnrichment = useTeamEnrichmentProvider(enrichmentTeam?.id, 'harmonic')
  const fallbackCanManageTeamEnrichment = enrichmentTeam?.role === 'owner' || enrichmentTeam?.role === 'admin'
  const canManageTeamEnrichment = teamEnrichment.status?.canManage ?? fallbackCanManageTeamEnrichment
  const extensionUnavailable = extensionReady === false

  const affinityMappingCount = useLiveQuery(
    () => db.crmMappings.where('crmName').equals('affinity').count(),
    [],
  ) ?? 0

  const handleConnect = async () => {
    const key = affinityKey.trim()
    if (!key) return
    setConnecting(true)
    setActionError(null)
    setActionSuccess(null)
    const result = await connect('affinity', key)
    setConnecting(false)
    if (result.ok) {
      setAffinityKey('')
      setActionSuccess('Connected to Affinity as ' + result.connection.userName)
    } else {
      setActionError(result.error)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    setActionError(null)
    setActionSuccess(null)
    const result = await test('affinity')
    setTesting(false)
    if (result.ok) setActionSuccess('Connection verified.')
    else setActionError(result.message || 'Test failed')
  }

  const handleDisconnect = async () => {
    const msg = affinityMappingCount > 0
      ? `Disconnect Affinity? Your API key and ${affinityMappingCount} cached contact ${affinityMappingCount === 1 ? 'mapping' : 'mappings'} will be removed from this device.`
      : 'Disconnect Affinity? Your API key will be removed from this device.'
    if (!confirm(msg)) return
    setActionError(null)
    setActionSuccess(null)
    const result = await disconnect('affinity')
    if (result.ok) setActionSuccess('Disconnected.')
    else setActionError(result.error)
  }

  const handleSaveTeamEnrichment = async () => {
    const key = teamEnrichmentKey.trim()
    if (!key) return
    setActionError(null)
    setActionSuccess(null)
    const result = await teamEnrichment.saveApiKey(key)
    if (result.ok) {
      setTeamEnrichmentKey('')
      setActionSuccess('Team enrichment key saved.')
    } else {
      setActionError(result.error)
    }
  }

  const handleDisableTeamEnrichment = async () => {
    if (!confirm('Disable team enrichment? Slash will remove the provider key and stop minting add-in lookup access for this team.')) return
    setActionError(null)
    setActionSuccess(null)
    const result = await teamEnrichment.disable()
    if (result.ok) setActionSuccess('Team enrichment disabled.')
    else setActionError(result.error)
  }

  // Legacy: prior to the Supabase move, this page had an "Activity Enrichment"
  // panel for creating/verifying 14 custom fields in Affinity. Those aggregates
  // now live in Supabase (linkedin_person_activity + upsert_person_activity).
  // We keep the Disable button on this page for users who connected before the
  // move, but no new enablement path — connecting Affinity now implicitly
  // starts syncing (enrichmentEnabled defaults to true on connect).
  const {
    config: enrichmentConfig,
    disable: disableEnrichmentFeature,
  } = useEnrichment(affinity?.status === 'active' ? 'affinity' : null)

  const handleDisableEnrichment = async () => {
    if (
      !confirm(
        'Stop syncing LinkedIn activity to Affinity for this account? You can re-enable by reconnecting Affinity. The 14 legacy activity fields in your Affinity workspace (if any) will stay intact — Slash stopped updating them when activity moved to Supabase.',
      )
    ) return
    setActionError(null)
    setActionSuccess(null)
    const result = await disableEnrichmentFeature()
    if (result.ok) setActionSuccess('LinkedIn activity sync disabled for Affinity.')
    else setActionError(result.error || 'Failed to disable')
  }

  // Invitation backfill — pulls current pending-sent invites and emits any
  // that haven't been synced to Affinity/Supabase yet. One-way catch-up for
  // events that fired before the CRM was connected. Cannot recover historical
  // accepted/withdrawn events — LinkedIn doesn't expose them.
  const [backfilling, setBackfilling] = useState(false)
  const handleBackfillInvitations = async () => {
    setActionError(null)
    setActionSuccess(null)
    setBackfilling(true)
    try {
      const result = await backfillInvitations('affinity')
      if (result.ok) {
        const { newEvents = 0, pendingCount = 0 } = result
        setActionSuccess(
          newEvents === 0
            ? `No new invitation events to sync (${pendingCount} pending invite${pendingCount === 1 ? '' : 's'} already up to date).`
            : `Synced ${newEvents} invitation event${newEvents === 1 ? '' : 's'} to Affinity.`,
        )
      } else if (result.reason === 'not_connected') {
        setActionError('Connect Affinity first.')
      } else {
        setActionError(`Backfill failed: ${result.message || result.reason || 'unknown error'}`)
      }
    } catch (e) {
      setActionError((e as Error)?.message || 'Backfill failed')
    } finally {
      setBackfilling(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-400">
        Loading CRM status...
      </div>
    )
  }

  return (
    <div className="relative flex flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-900">CRM Connections</h1>
        <p className="text-sm text-zinc-500">
          Connect browser-local CRM sync and enrichment. Affinity keys stay
          on this device; Harmonic keys are stored server-side in
          Supabase Vault and never exposed to the Chrome add-in.
        </p>
      </header>

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {extensionUnavailable && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          The Slash extension is required for browser-local Affinity CRM sync.
          Team enrichment settings can still be managed here.
        </div>
      )}

      {(actionError || actionSuccess) && (
        <p
          className={
            'rounded-lg px-3 py-2 text-xs ' +
            (actionError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700')
          }
        >
          {actionError || actionSuccess}
        </p>
      )}

      <div className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium text-zinc-900">Team enrichment provider</h3>
            <p className="mt-1 text-xs text-zinc-500">
              {usingSoloEnrichment
                ? 'One Harmonic API key covers your Slash workspace. If you later join a shared team, Slash will use that team key instead.'
                : `One Harmonic API key covers ${sharedTeam?.name ?? 'the shared team'}. The key is stored server-side and the Chrome add-in only receives short-lived lookup access.`}
            </p>
            {!enrichmentTeam && (
              <p className="mt-2 text-xs text-amber-700">
                Your Slash workspace is still being created. Refresh in a moment if this does not update.
              </p>
            )}
            {teamEnrichment.error && (
              <p className="mt-2 text-xs text-red-600">{teamEnrichment.error}</p>
            )}
          </div>
          {teamEnrichment.status?.connected && (
            <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              Connected
            </span>
          )}
          {teamEnrichment.status && !teamEnrichment.status.connected && (
            <span className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500">
              Not connected
            </span>
          )}
        </div>

        {enrichmentTeam && (
          <div className="mt-4">
            {teamEnrichment.loading ? (
              <p className="text-xs text-zinc-400">Loading enrichment status...</p>
            ) : (
              <>
                {teamEnrichment.status?.connected && (
                  <div className="mb-3 text-xs text-zinc-500">
                    Harmonic key ending in{' '}
                    <span className="font-mono">••••{teamEnrichment.status.keyLast4}</span>
                    {teamEnrichment.status.updatedAt && (
                      <> · updated {formatRelative(Date.parse(teamEnrichment.status.updatedAt))}</>
                    )}
                  </div>
                )}

                {canManageTeamEnrichment ? (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-zinc-600">
                      Harmonic API key
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={teamEnrichmentKey}
                        onChange={(e) => {
                          setTeamEnrichmentKey(e.target.value)
                          if (actionError || actionSuccess) {
                            setActionError(null)
                            setActionSuccess(null)
                          }
                        }}
                        placeholder={teamEnrichment.status?.connected ? 'Paste a new key to rotate' : 'Paste your Harmonic API key'}
                        disabled={teamEnrichment.saving}
                        autoComplete="off"
                        spellCheck={false}
                        className="flex-1 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm focus:border-zinc-500 focus:outline-none"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTeamEnrichment() }}
                      />
                      <button
                        onClick={handleSaveTeamEnrichment}
                        disabled={teamEnrichment.saving || !teamEnrichmentKey.trim()}
                        className="rounded-md bg-zinc-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
                      >
                        {teamEnrichment.saving ? 'Saving...' : teamEnrichment.status?.connected ? 'Rotate' : 'Save'}
                      </button>
                      {teamEnrichment.status?.connected && (
                        <button
                          onClick={handleDisableTeamEnrichment}
                          disabled={teamEnrichment.disabling}
                          className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                        >
                          {teamEnrichment.disabling ? 'Disabling...' : 'Disable'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">
                      {usingSoloEnrichment
                        ? 'You own this solo workspace, so you can manage this key.'
                        : 'Only team admins and owners can manage this key.'}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500">
                    Ask a team admin or owner to configure the enrichment provider.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Affinity card */}
      {!extensionUnavailable && (
        <div
          className={
            'rounded-xl border p-6 ' +
            (affinity?.status === 'invalid'
              ? 'border-red-300 bg-red-50/50'
              : 'border-zinc-200 bg-white')
          }
        >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Affinity wordmark — swap for real SVG when available */}
            <div className="flex h-9 w-9 items-center justify-center rounded bg-[#0f172a] text-sm font-bold text-white">
              A
            </div>
            <div>
              <h3 className="font-medium text-zinc-900">Affinity</h3>
              {affinity ? (
                <div className="mt-0.5 space-y-0.5 text-xs text-zinc-500">
                  <div>
                    {affinity.userName}{' '}
                    <span className="opacity-70">({affinity.userEmail})</span>
                  </div>
                  <div>
                    <span className="font-mono">{affinity.tenantSubdomain}</span>
                    .affinity.co · verified {formatRelative(affinity.verifiedAt)}
                  </div>
                </div>
              ) : (
                <p className="mt-0.5 text-xs text-zinc-500">Not connected</p>
              )}
            </div>
          </div>

          {affinity?.status === 'active' && (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              Connected
            </span>
          )}
          {affinity?.status === 'invalid' && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              Key invalid
            </span>
          )}
        </div>

        {/* Connected state: Test + Disconnect */}
        {affinity && affinity.status === 'active' && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleTest}
              disabled={testing}
              className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            >
              {testing ? 'Testing...' : 'Test connection'}
            </button>
            <button
              onClick={handleDisconnect}
              className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Disconnect
            </button>
          </div>
        )}

        {/* LinkedIn activity sync status. Pre-Supabase this panel let users
            create/verify/repair 14 Affinity activity fields; those fields are
            now ignored by Slash and activity aggregates live in Supabase. We
            only show anything here for users who had the legacy feature on —
            giving them a one-way off-ramp. */}
        {affinity?.status === 'active' && enrichmentConfig?.enabled && (
          <div className="mt-5 border-t border-zinc-100 pt-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-sm font-medium text-zinc-900">LinkedIn activity sync</h4>
                <p className="mt-1 text-xs text-zinc-500">
                  Slash logs LinkedIn messages as interactions on matched Affinity
                  contacts. Aggregate activity (last sent, messages sent/received,
                  etc.) is stored inside Slash — not as Affinity fields — so the
                  contact timeline stays clean.
                </p>
                {enrichmentConfig.fieldIds && (
                  <p className="mt-2 text-xs text-zinc-400">
                    Heads up: we used to create 14 <em>LinkedIn …</em> fields on
                    your Affinity Person records. Those fields aren't updated
                    anymore — you can delete them in Affinity at your convenience.
                  </p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Active
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={handleBackfillInvitations}
                disabled={backfilling}
                title="Fires 'invitation sent' and 'accepted' notes for pending invites that aren't yet in Affinity. New invite events sync automatically going forward — this is for catching up anything that happened before the CRM was connected."
                className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              >
                {backfilling ? 'Backfilling...' : 'Backfill invitation history'}
              </button>
              <button
                onClick={handleDisableEnrichment}
                className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
              >
                Stop syncing to Affinity
              </button>
            </div>
          </div>
        )}

        {/* Disconnected OR invalid: show key input */}
        {(!affinity || affinity.status === 'invalid') && (
          <div className="mt-4 space-y-2">
            {affinity?.status === 'invalid' && (
              <p className="text-xs text-red-600">
                Your API key is no longer valid. Paste a new one below to reconnect.
              </p>
            )}
            <label className="block text-xs font-medium text-zinc-600">
              API key
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                value={affinityKey}
                onChange={(e) => {
                  setAffinityKey(e.target.value)
                  if (actionError || actionSuccess) {
                    setActionError(null)
                    setActionSuccess(null)
                  }
                }}
                placeholder="Paste your Affinity API key"
                disabled={connecting}
                autoComplete="off"
                spellCheck={false}
                className="flex-1 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm focus:border-zinc-500 focus:outline-none"
                onKeyDown={(e) => { if (e.key === 'Enter') handleConnect() }}
              />
              <button
                onClick={handleConnect}
                disabled={connecting || !affinityKey.trim()}
                className="rounded-md bg-zinc-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
              >
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              Need a key?{' '}
              <a
                href="https://support.affinity.co/hc/en-us/articles/360032633992"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-zinc-700"
              >
                How to generate an Affinity API key
              </a>
              .
            </p>
          </div>
        )}

        </div>
      )}
    </div>
  )
}
